import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import appointmentService from '../../../services/appointment.service';
import { showError, showSuccess } from '../../../utils/ecxSwal';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import ThankYouFollowupsSkeleton from './subparts/ThankYouFollowupsSkeleton';
import PatientEngagementNav from './subparts/PatientEngagementNav';
import SurveyFollowupsPanel from './subparts/SurveyFollowupsPanel';
import {
    THANK_YOU_CHANNELS,
    buildDefaultChannelSelection,
    buildThankYouSendJobs,
    DEFAULT_THANK_YOU_REMINDER_TYPE,
    formatAppointmentDateTime,
    formatPatientName,
    getChannelState,
    getRowChannelSelection,
    getRowKey,
    getSendableRowKeys,
    isRowSendable,
    resolvePreferredChannel,
} from './subparts/thankYouFollowupChannel';
import {
    buildThankYouFollowupsPrintSubtitle,
    printThankYouFollowupsList,
} from './subparts/thankYouFollowupsPrint';
import './ThankYouAndSurveys.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const ENGAGEMENT_TABS = [
    { id: 'thank-you', label: 'Thank You', icon: 'bi-heart' },
    { id: 'surveys', label: 'Surveys', icon: 'bi-clipboard-check' },
];

function ThankYouAndSurveys() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [activeTab, setActiveTab] = useState('thank-you');
    const [loadedTabs, setLoadedTabs] = useState(() => new Set(['thank-you']));
    const [surveyRefreshToken, setSurveyRefreshToken] = useState(0);
    const [surveyTotalElements, setSurveyTotalElements] = useState(null);

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [channelSelections, setChannelSelections] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());
    const [sending, setSending] = useState(false);
    const selectAllRef = useRef(null);

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const fetchRecords = useCallback(
        async (page, size, options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            try {
                const res = await appointmentService.getThankYouFollowups(clinicId, page, size);
                const data = res.data;
                const content = data.content || [];

                setRecords(content);
                setPageNumber(data.number ?? page);
                setPageSize(data.size ?? size);
                setTotalPages(data.totalPages ?? 0);
                setTotalElements(data.totalElements ?? 0);
                setChannelSelections(
                    content.reduce((acc, record) => {
                        acc[getRowKey(record)] = buildDefaultChannelSelection(record);
                        return acc;
                    }, {})
                );
                setSelectedRowKeys(new Set(getSendableRowKeys(content)));
            } catch (err) {
                console.error('Failed to load thank-you followups', err);
                toast.error('Failed to load thank-you followups');
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [clinicId]
    );

    const thankYouLoaded = loadedTabs.has('thank-you');

    useEffect(() => {
        if (!thankYouLoaded) {
            return;
        }

        fetchRecords(pageNumber, pageSize);
    }, [fetchRecords, pageNumber, pageSize, thankYouLoaded]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setLoadedTabs((prev) => new Set([...prev, tabId]));
    };

    const handleRefresh = async () => {
        if (activeTab === 'surveys') {
            setSurveyRefreshToken((token) => token + 1);
            return;
        }

        setIsRefreshing(true);
        try {
            await fetchRecords(pageNumber, pageSize, { silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPageNumber(0);
    };

    const handleChannelSelect = (rowKey, channelKey, disabled) => {
        if (disabled) {
            return;
        }

        setChannelSelections((prev) => ({
            ...prev,
            [rowKey]: channelKey,
        }));
    };

    const sendableRowKeys = useMemo(() => getSendableRowKeys(records), [records]);

    const allSendableSelected =
        sendableRowKeys.length > 0 && sendableRowKeys.every((rowKey) => selectedRowKeys.has(rowKey));

    const someSendableSelected = sendableRowKeys.some((rowKey) => selectedRowKeys.has(rowKey));

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSendableSelected && !allSendableSelected;
        }
    }, [someSendableSelected, allSendableSelected]);

    const selectedSendJobs = useMemo(
        () => buildThankYouSendJobs(records, selectedRowKeys, channelSelections),
        [records, selectedRowKeys, channelSelections]
    );

    const handleToggleSelectAll = () => {
        if (allSendableSelected) {
            setSelectedRowKeys(new Set());
            return;
        }

        setSelectedRowKeys(new Set(sendableRowKeys));
    };

    const handleToggleRow = (rowKey, record) => {
        if (!isRowSendable(record)) {
            return;
        }

        setSelectedRowKeys((prev) => {
            const next = new Set(prev);

            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }

            return next;
        });
    };

    const handleSendThankYou = async () => {
        if (!clinicId) {
            return;
        }

        const jobs = selectedSendJobs;

        if (!jobs.length) {
            toast.info('Select at least one patient with an available email or SMS channel.');
            return;
        }

        const emailCount = jobs.filter((job) => job.mediumType === 'EMAIL').length;
        const smsCount = jobs.filter((job) => job.mediumType === 'SMS').length;
        const channelSummary = [
            emailCount ? `${emailCount} email${emailCount === 1 ? '' : 's'}` : null,
            smsCount ? `${smsCount} SMS` : null,
        ]
            .filter(Boolean)
            .join(' and ');

        const result = await Swal.fire({
            title: 'Send thank-you reminders?',
            html: `Queue <strong>${channelSummary}</strong> for the selected patients.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Send',
            cancelButtonText: 'Cancel',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'btn btn-primary rounded-pill px-3 me-2',
                cancelButton: 'btn btn-outline-secondary rounded-pill px-3 ecx-swal__btn ecx-swal__btn--cancel',
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        setSending(true);

        try {
            const response = await appointmentService.sendThankYouFollowups(
                clinicId,
                jobs.map((job) => ({
                    patientId: job.patientId,
                    appointmentId: job.appointmentId,
                    mediumType: job.mediumType,
                    reminderType: DEFAULT_THANK_YOU_REMINDER_TYPE,
                }))
            );

            const { queued = 0, failed = 0 } = response.data || {};

            if (queued > 0) {
                showSuccess({
                    title: 'Thank You Queued',
                    text:
                        failed > 0
                            ? `${queued} message${queued === 1 ? '' : 's'} queued. ${failed} failed.`
                            : `${queued} thank-you message${queued === 1 ? '' : 's'} queued for delivery.`,
                });
                await fetchRecords(pageNumber, pageSize, { silent: true });
            } else {
                showError({
                    title: 'Send Failed',
                    text: 'Could not queue thank-you messages for the selected patients.',
                });
            }
        } catch (error) {
            console.error('Failed to queue thank-you reminders', error);
            showError({
                title: 'Send Failed',
                text: 'Could not queue thank-you messages for the selected patients.',
            });
        } finally {
            setSending(false);
        }
    };

    const toolbarMeta = useMemo(
        () => ({
            pageLabel: `Page ${totalPages === 0 ? 0 : pageNumber + 1} of ${Math.max(totalPages, 1)}`,
            totalLabel: `${totalElements} patient${totalElements === 1 ? '' : 's'}`,
        }),
        [pageNumber, totalPages, totalElements]
    );

    const description = useMemo(() => {
        if (activeTab === 'surveys') {
            return surveyTotalElements
                ? `Confirmed appointments from the last 7 days · ${surveyTotalElements} total record${surveyTotalElements === 1 ? '' : 's'}`
                : 'Send post-visit surveys to patients with confirmed appointments from the last 7 days';
        }

        return totalElements
            ? `Confirmed appointments from the last 7 days · ${totalElements} total record${totalElements === 1 ? '' : 's'}`
            : 'Send thank-you messages and surveys to patients after visits';
    }, [activeTab, totalElements, surveyTotalElements]);

    const tabCounts = useMemo(
        () => ({
            'thank-you': thankYouLoaded && !loading ? totalElements : null,
            surveys: surveyTotalElements,
        }),
        [thankYouLoaded, loading, totalElements, surveyTotalElements]
    );

    const handlePrint = () => {
        const printed = printThankYouFollowupsList({
            records,
            channelSelections,
            title: 'Patient Engagement — Thank You',
            subtitle: buildThankYouFollowupsPrintSubtitle({
                recordsCount: records.length,
                totalElements,
                pageNumber,
                totalPages,
            }),
        });

        if (!printed) {
            toast.error('Unable to open print preview. Please try again.');
        }
    };

    return (
        <div className="ecx-page thank-you-surveys-page ecx-page--full-width">
            <SubHeader
                title="Patient Engagement"
                description={description}
                icon="bi-person-hearts"
                containerClass="w-100 px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={handleRefresh}
                    disabled={(activeTab === 'thank-you' && (loading || sending)) || isRefreshing}
                    title={activeTab === 'surveys' ? 'Refresh survey followups' : 'Refresh thank-you followups'}
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`}></i>
                    Refresh
                </button>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="patient-engagement-tabs-wrap">
                    <PatientEngagementNav
                        tabs={ENGAGEMENT_TABS}
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                        counts={tabCounts}
                    />
                </div>

                <div className="ecx-page-card ecx-page-panel thank-you-surveys-panel">
                    {loadedTabs.has('thank-you') && (
                        <div className={activeTab === 'thank-you' ? 'd-flex flex-column h-100' : 'd-none'}>
                    <div className="ecx-page-panel__header">
                        <div className="thank-you-surveys-toolbar">
                            <div className="thank-you-surveys-toolbar__meta">
                                <h5 className="mb-0 fw-semibold text-dark">Thank You</h5>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-list-ul"></i>
                                    {toolbarMeta.totalLabel}
                                </span>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-layers"></i>
                                    {toolbarMeta.pageLabel}
                                </span>
                            </div>

                            <div className="thank-you-surveys-toolbar__controls">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary rounded-pill"
                                    onClick={handleSendThankYou}
                                    disabled={loading || sending || selectedSendJobs.length === 0}
                                    aria-busy={sending}
                                >
                                    {sending ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-1"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-send me-1" />
                                            Send Thank You
                                            {selectedSendJobs.length > 0 ? ` (${selectedSendJobs.length})` : ''}
                                        </>
                                    )}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handlePrint}
                                    disabled={loading || records.length === 0}
                                >
                                    <i className="bi bi-printer me-1"></i>
                                    Print
                                </button>

                                <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                    Show
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '5rem' }}
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                        disabled={loading}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                    entries
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__body d-flex flex-column p-0 position-relative">
                        {sending && (
                            <div className="thank-you-surveys-sending-overlay" role="status" aria-live="polite">
                                <div className="thank-you-surveys-sending-overlay__content">
                                    <span
                                        className="spinner-border text-primary thank-you-surveys-sending-overlay__spinner"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    <p className="thank-you-surveys-sending-overlay__title mb-1">
                                        Sending thank-you messages
                                    </p>
                                    <p className="thank-you-surveys-sending-overlay__subtitle mb-0">
                                        Queueing {selectedSendJobs.length} message
                                        {selectedSendJobs.length === 1 ? '' : 's'}...
                                    </p>
                                </div>
                            </div>
                        )}
                        {loading ? (
                            <ThankYouFollowupsSkeleton count={pageSize > 8 ? 8 : pageSize} />
                        ) : records.length > 0 ? (
                            <>
                                <div className="table-responsive thank-you-surveys-table-wrap">
                                    <table className="table table-hover align-middle mb-0 thank-you-surveys-table">
                                        <thead>
                                            <tr>
                                                <th className="thank-you-surveys-select-col ps-3">
                                                    <input
                                                        ref={selectAllRef}
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={allSendableSelected}
                                                        disabled={sendableRowKeys.length === 0 || loading || sending}
                                                        onChange={handleToggleSelectAll}
                                                        aria-label="Select all patients on this page"
                                                    />
                                                </th>
                                                <th>Patient</th>
                                                <th>Email</th>
                                                <th>Cell Phone</th>
                                                <th>Home Phone</th>
                                                <th>Appointment</th>
                                                {THANK_YOU_CHANNELS.map((channel) => (
                                                    <th key={channel.key} className="text-center">
                                                        {channel.label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {records.map((record) => {
                                                const rowKey = getRowKey(record);
                                                const patientName = formatPatientName(record);
                                                const preferredChannel = resolvePreferredChannel(record);
                                                const selection = getRowChannelSelection(channelSelections, rowKey, record);
                                                const rowSendable = isRowSendable(record);
                                                const rowSelected = selectedRowKeys.has(rowKey);

                                                return (
                                                    <tr key={rowKey} className={rowSelected ? 'thank-you-surveys-row--selected' : ''}>
                                                        <td className="thank-you-surveys-select-col ps-3">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={rowSelected}
                                                                disabled={!rowSendable || loading || sending}
                                                                onChange={() => handleToggleRow(rowKey, record)}
                                                                aria-label={`Select ${patientName}`}
                                                            />
                                                        </td>
                                                        <td className="thank-you-surveys-patient">
                                                            <Link
                                                                to={`/patient-files/${record.id}`}
                                                                className="thank-you-surveys-patient-link fw-semibold"
                                                            >
                                                                {patientName}
                                                            </Link>
                                                        </td>
                                                        <td className="thank-you-surveys-contact">
                                                            {record.email || <span className="text-muted">—</span>}
                                                        </td>
                                                        <td className="thank-you-surveys-contact">
                                                            {record.cellPhone || <span className="text-muted">—</span>}
                                                        </td>
                                                        <td className="thank-you-surveys-contact">
                                                            {record.homePhone || <span className="text-muted">—</span>}
                                                        </td>
                                                        <td className="thank-you-surveys-appointment">
                                                            {formatAppointmentDateTime(record)}
                                                        </td>
                                                        {THANK_YOU_CHANNELS.map((channel) => {
                                                            const state = getChannelState(record, channel.key);
                                                            const isPreferred = preferredChannel === channel.key;
                                                            const checked = selection === channel.key;
                                                            const title = state.disabled
                                                                ? record.isThankyou === false
                                                                    ? 'Thank-you reminders disabled in communication preferences'
                                                                    : !state.available
                                                                      ? `No ${channel.label.toLowerCase()} contact on file`
                                                                      : `${channel.label} disabled in communication preferences`
                                                                : isPreferred
                                                                  ? `Preferred ${channel.label.toLowerCase()} channel`
                                                                  : channel.label;

                                                            return (
                                                                <td key={`${rowKey}-${channel.key}`} className="text-center">
                                                                    <div
                                                                        className={`thank-you-surveys-channel ${
                                                                            isPreferred ? 'is-preferred' : ''
                                                                        } ${state.disabled ? 'is-disabled' : ''}`}
                                                                        title={title}
                                                                    >
                                                                        <input
                                                                            type="radio"
                                                                            name={`thank-you-channel-${rowKey}`}
                                                                            className="form-check-input"
                                                                            checked={checked}
                                                                            disabled={state.disabled || loading || sending}
                                                                            onChange={() =>
                                                                                handleChannelSelect(rowKey, channel.key, state.disabled)
                                                                            }
                                                                            aria-label={`${channel.label} for ${patientName}`}
                                                                        />
                                                                    </div>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div className="ecx-page-pagination border-top p-3 mt-auto">
                                        <Pagination
                                            currentPage={pageNumber}
                                            totalPages={totalPages}
                                            totalElements={totalElements}
                                            pageSize={pageSize}
                                            onPageChange={setPageNumber}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="ecx-page-empty flex-grow-1">
                                <div className="ecx-page-empty__icon">
                                    <i className="bi bi-heart"></i>
                                </div>
                                <h5 className="text-dark fw-semibold mb-2">No thank-you followups</h5>
                                <p className="mb-0">
                                    There are no confirmed appointments from the last 7 days.
                                </p>
                            </div>
                        )}
                    </div>
                        </div>
                    )}

                    {loadedTabs.has('surveys') && (
                        <div className={activeTab === 'surveys' ? 'd-flex flex-column h-100' : 'd-none'}>
                            <SurveyFollowupsPanel
                                refreshToken={surveyRefreshToken}
                                onCountUpdate={setSurveyTotalElements}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ThankYouAndSurveys;
